'use client';

import { useCallback, useState } from 'react';

/**
 * Kesin kayıt (final registration) from inside the portal — BTK 9482.
 *
 * <p>Until now the only way to confirm was the link in the invitation mail, so a
 * participant who deleted it, or who was simply already on the site, had no way
 * to complete their registration. This is the same act against the same
 * quota-guarded endpoint, taken from the card the participant is looking at.
 *
 * <p>Logic lives here rather than in a shared control because the three activity
 * blocks (default, BTK, row) each own their own buttons: sharing the call keeps
 * one behaviour, sharing a widget would fight three designs.
 */

/** The entry status that means "invited, waiting for the participant's answer". */
export const AWAITING_FINAL_REGISTRATION = 'AWAITING';

/** What the backend says happened; every one of these is worth showing. */
export type FinalRegistrationResult =
  | 'DONE'
  | 'ALREADY_DONE'
  | 'QUOTA_FULL'
  | 'NOT_ACTIONABLE'
  | 'ERROR';

export interface FinalRegistrationState {
  /** A request is in flight; both buttons should be disabled. */
  busy: boolean;
  /** Result of the last attempt, or null before one is made. */
  result: FinalRegistrationResult | null;
  /** The entry status after a successful action, so the card can re-render. */
  status: string | null;
  confirm: () => Promise<FinalRegistrationResult>;
  cancel: () => Promise<FinalRegistrationResult>;
}

export function useFinalRegistration(eventId: number | string): FinalRegistrationState {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<FinalRegistrationResult | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const post = useCallback(
    async (action: 'CONFIRM' | 'CANCEL'): Promise<FinalRegistrationResult> => {
      setBusy(true);
      try {
        const res = await fetch(
          `/api/activities/${encodeURIComponent(String(eventId))}/final-registration`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action }),
          },
        );
        if (!res.ok) {
          setResult('ERROR');
          return 'ERROR';
        }
        const body = (await res.json()) as { result?: string; status?: string } | null;
        const outcome = (body?.result as FinalRegistrationResult) ?? 'ERROR';
        setResult(outcome);
        if (body?.status) setStatus(body.status);
        return outcome;
      } catch {
        setResult('ERROR');
        return 'ERROR';
      } finally {
        setBusy(false);
      }
    },
    [eventId],
  );

  const confirm = useCallback(() => post('CONFIRM'), [post]);
  const cancel = useCallback(() => post('CANCEL'), [post]);

  return { busy, result, status, confirm, cancel };
}
